import { useNavigate } from "react-router-dom";
import MainContainer from "../../../layout/MainContainer";
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Switch, Box, IconButton, Button } from "@mui/material";
import SearchBar from "../../../components/SearchBar";
import { useEffect, useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import { useDeleteVoiceMutation, useLazyGetVoiceQuery } from "../../../services/aiVoice";
import { showError, showToast } from "../../../constants/toasts";
import DeleteConfirmModal from "../../../Modals/deleteConfirmModal";
import { VOICE_TYPE_NAME } from "../../../utils/enums";
import { API_URL } from "../../../constants/url";
import Panigation from "../../../components/Panigation";
import { isValidInput } from "../../../utils/validations";
import IfCan from "../../../components/IfCan";

const VoiceLibrary = () => {

    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [page, setPage] = useState(1);
    const [data, setData] = useState<any[]>([]);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
    const [getVoice] = useLazyGetVoiceQuery()
    const [deleteVoice] = useDeleteVoiceMutation()
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);


    const getVoiceList = async () => {
        setIsLoading(true);
        try {
            const response = await getVoice({
                length: 10,
                start: page,
                search: debouncedSearchTerm,
            }).unwrap();
            if (response.status === 200) {
                setData(response?.data?.response_object || []);
                setTotalCount(response?.data?.total_records || 0);
            }
            setIsLoading(false);
        } catch (error) {
            console.log(error);
            setIsLoading(false);
        }
    };


    const handleDelete = async () => {
        if (!selectedId) return;
        try {
            await deleteVoice({ id: selectedId }).unwrap();
            showToast("Voice deleted successfully");
            getVoiceList()
        } catch (e: any) {
            showError(e?.data?.message || "Failed to delete Voice");
        }
        setDeleteOpen(false);
        setSelectedId(null);
    };
    useEffect(() => {
        getVoiceList()
    }, [page, debouncedSearchTerm])

    const columns = [
        { id: 'voiceType', label: 'Voice Type' },
        { id: 'sample', label: 'Test Sample' },
        { id: 'ageGroup', label: 'Age Group' },
        { id: 'action', label: 'Action' },
    ];

    const [searchTerm, setSearchTerm] = useState("");
    return (
        <>
            <div className="main_title">
                <h1>Voice Library</h1>
                <p>
                    <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Voice Library
                </p>
            </div>

            <div className="cards">
                <div className="table_header">
                    <div className="left_s">
                        <Box className="search_box">
                            <SearchBar
                                placeholder="Search..."
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setDebouncedSearchTerm}
                                value={searchTerm}
                                onCross={() => setSearchTerm("")}
                                onChange={(val: any) => {
                                    if (isValidInput(val.target.value)) {
                                        setSearchTerm(val.target.value);
                                        setPage(1);
                                    }
                                }}
                            />
                        </Box>
                    </div>
                    <div className="right_s">
                        <IfCan module="AI Behavior Language" action="add_edit">
                            <Button className="btn btn_primary" onClick={() => navigate("/voice-library/add")}><AddIcon /> Add Voice</Button>
                        </IfCan>
                    </div>
                </div>
                <TableContainer className="table_container">
                    <Table>
                        <TableHead>
                            <TableRow hover>
                                {columns.map((column) => (
                                    <TableCell key={column.id}>{column.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={4} style={{ textAlign: 'center' }}>
                                        Loading...
                                    </TableCell>
                                </TableRow>
                            ) : data.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} style={{ textAlign: 'center' }}>
                                        No data found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data.map((row: any) => (
                                    <TableRow hover key={row.id}>
                                        <TableCell>{VOICE_TYPE_NAME[row.voice_type] || 'Unknown'}</TableCell>

                                        <TableCell>
                                            {row.audio ? (
                                                <audio controls style={{ width: '170px' }}>
                                                    <source src={`${API_URL}/media/${row.audio}`} type="audio/mpeg" />
                                                    Your browser does not support the audio element.
                                                </audio>
                                            ) : (
                                                <span style={{ color: '#999' }}>No audio file</span>
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            {row.age_group ? `${row.age_group.start_range}-${row.age_group.end_range}` : 'N/A'}
                                        </TableCell>

                                        <TableCell>
                                            <div className="table_actions v2">
                                                <Box>
                                                    <IfCan module="AI Behavior Language" action="add_edit">
                                                        <IconButton onClick={() => navigate(`/voice-library/edit/${row.id}`)}>
                                                            <img src="/static/images/edit2_icon.svg" alt="Edit" />
                                                        </IconButton>
                                                    </IfCan>
                                                </Box>
                                                <Box>
                                                    <IfCan module="AI Behavior Language" action="delete">
                                                        <IconButton onClick={() => {
                                                            setSelectedId(row.id);
                                                            setDeleteOpen(true);
                                                        }}>
                                                            <img src="/static/images/trash2_icon.svg" alt="Delete" />
                                                        </IconButton>
                                                    </IfCan>
                                                </Box>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>

                    </Table>
                </TableContainer>
                {totalCount > 0 && (
                    <Panigation
                        module={data}
                        totalCount={totalCount}
                        page={page}
                        setPage={setPage}
                        totalPages={Math.ceil(totalCount / 10)}
                        onPageChange={(newPage: number) => setPage(newPage)}
                    />
                )}
            </div>
            <DeleteConfirmModal
                open={deleteOpen}
                setOpen={setDeleteOpen}
                handleDelete={handleDelete}
                title="Are you sure you want to delete this Voice?"
                message="This action cannot be undone."
            />
        </>
    );

};

export default VoiceLibrary;
